import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import useSWR from 'swr';
import { GUIDED_CONNECTION_WIDTHS, ThemeModes } from '@hookdeck/theme';
import { DestinationType } from '../../../../../../../typings/Integration.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import LINKS from '../../../../configs/links';
import Badge from '../../../common/base/Badge';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Container from '../../../common/base/Container';
import Icon from '../../../common/base/Icon';
import Link from '../../../common/base/Link';
import Text from '../../../common/base/Text';
import { Div } from '../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useEventList from '../../../hooks/useEventList';
import { useGuidedStepScroll } from '../../../hooks/useGuidedStepScroll';
import useLocalStorage from '../../../hooks/useLocalStorage';
import useRequestList from '../../../hooks/useRequestList';
import FullscreenLayout from '../../../layouts/FullscreenLayout';
import { StyledProgress } from '../Connections/Create/CreateView';
import { DashboardContext } from '../DashboardContext';
import GuidedConnectionNav from './GuidedConnectionNav';
import AddDestination, { SelectDestinationType } from './Steps/AddDestination';
import AddRules from './Steps/AddRules';
import AddSource from './Steps/AddSource';
import ConnectCLI from './Steps/ConnectCLI';
import InstallCLI from './Steps/InstallCLI';
import PreviewEvents from './Steps/PreviewEvents';
import PreviewRequests from './Steps/PreviewRequests';
import RunHookdeckLogin from './Steps/RunHookdeckLogin';
import SetMaxDeliveryRate from './Steps/SetMaxDeliveryRate';
import TriggerRequest from './Steps/TriggerRequest';
const steps = [{
  key: 'source',
  nav_label: 'Define event source',
  label: 'Define your request source',
  label_types: 'Define your event source',
  description: 'Name and configure the publisher of your HTTP requests.',
  description_types: 'Configure the Hookdeck endpoint representing your HTTP request producer.',
  icon: 'source' as const,
  Component: AddSource,
  reference: {
    title: 'Sources',
    image: (theme_mode: ThemeModes) => `/images/guided-connection/source-illustration-${theme_mode}.svg`,
    text: 'Sources represent the producer of your requests. In Hookdeck, any service that sends HTTP requests can be defined as a source. They can be webhooks, SDKs, devices, customer facing endpoints, or other services.',
    link: LINKS.product_docs.sources
  }
}, {
  key: 'trigger_request',
  label: 'Trigger HTTP requests to your Hookdeck source URL',
  description: 'The Hookdeck endpoint defined above will receive HTTP requests via a unique URL.',
  icon: 'send' as const,
  reveal: context => !!context.source,
  Component: TriggerRequest
}, {
  key: 'inspect_requests',
  label: 'Inspect inbound HTTP requests',
  description: 'View the headers, body, and metadata of the HTTP requests you triggered above.',
  icon: 'requests' as const,
  reveal: context => !!context.source,
  Component: PreviewRequests,
  full_width: true
}, {
  key: 'destination_type',
  nav_label: 'Define event destination',
  icon: 'destinations' as const,
  reveal: context => !!(context.requests_list && context.requests_list.count > 0),
  Component: SelectDestinationType,
  hide_header: true,
  full_width: true
}, {
  key: 'destination',
  label: 'Configure your destination',
  description: 'Provide details needed to connect the destination type selected above.',
  icon: 'connect' as const,
  reveal: context => !!(context.requests_list && context.requests_list.count > 0 && context.selected_destination_type),
  Component: AddDestination
}, {
  key: 'max_delivery_rate',
  label: 'Set your max event delivery rate',
  description: 'Optionally define an upper event delivery bound on your destination to shield it from unexpected spikes. Events received in excess will be queued and shown as Pending.',
  icon: 'rate_limit' as const,
  reveal: context => !!context.destination,
  conditional: context => context.destination?.type !== 'CLI',
  Component: SetMaxDeliveryRate,
  optional: true
}, {
  key: 'rules',
  nav_label: 'Define connection rules',
  label: 'Define your connection rules',
  description: 'Optionally configure rules that dictate the behavior of events being routed to your destination.',
  description_types: 'Rules are logic that dictate the behavior of outbound events to your destination.',
  icon: 'rule' as const,
  optional: true,
  Component: AddRules,
  reveal: context => !!context.destination,
  reference: {
    title: 'Rules',
    image: (theme_mode: ThemeModes) => `/images/guided-connection/connection-rules-illustration-${theme_mode}.svg`,
    text: 'Hookdeck offers five different pieces of instructional logic that can dictate the behavior of events being routed to a destination. Each rule can be customized to achieve a range of desired event outcomes.',
    link: LINKS.product_docs.rules
  }
}, {
  key: 'cli',
  label: 'Install the Hookdeck CLI',
  description: 'Follow the setup instructions below to receive events on your local server.',
  icon: 'rule' as const,
  Component: InstallCLI,
  reveal: context => !!context.destination,
  conditional: context => context.destination?.type === 'CLI',
  reference: {
    title: 'CLI',
    text: 'The Hookdeck CLI lets you forward events received on any Hookdeck connection to a local web server. Develop, test, and troubleshoot your integration with Hookdeck, all without interfering with your production setup.',
    link: LINKS.product_docs.cli
  }
}, {
  key: 'run_login',
  label: 'Authenticate the CLI',
  description: 'Run the command below in your terminal to authenticate the Hookdeck CLI.',
  icon: 'login' as const,
  Component: RunHookdeckLogin,
  reveal: context => !!context.destination,
  conditional: context => context.destination?.type === 'CLI'
}, {
  key: 'connect_cli',
  label: 'Connect CLI to your local server',
  description: 'With your local server running, start the Hookdeck CLI to forward events received on your Hookdeck source to your local server.',
  icon: 'connect' as const,
  Component: ConnectCLI,
  reveal: context => !!context.destination,
  conditional: context => context.destination?.type === 'CLI',
  skip_over: true
}, {
  key: 'events',
  nav_label: 'Inspect events',
  label: 'Inspect outbound events',
  reveal: context => !!context.webhook,
  description: 'Inspect the response of the events that have been routed to your destination.',
  description_types: 'View the response of events that Hookdeck has directed to your destination.',
  icon: 'events' as const,
  full_width: true,
  Component: PreviewEvents
}];
const GuidedCreateConnection: React.FC = () => {
  const $ = _c(72);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team,
    mutateHasConnection
  } = useContext(DashboardContext);
  const theme = useTheme();
  const [completed_step, setCompletedStep] = useLocalStorage(`${team.id}:progress:guided_connection_step`, steps[0].key);
  const [selected_destination_type, setSelectedDestinationType] = useLocalStorage(`${team.id}:progress:guided_connection_destination_type`, "HTTP");
  const [destination_type_committed, setDestinationTypeCommitted] = useLocalStorage(`${team.id}:progress:guided_connection_destination_type_committed`, false);
  const [preview_destination_type, setPreviewDestinationType] = useState(selected_destination_type);
  let t0;
  if ($[0] !== HookdeckAPI.sources) {
    t0 = () => HookdeckAPI.sources.list({
      limit: 1,
      disabled_at: null
    }).then(results => results.models.length > 0 ? results.models[0].authenticated ? HookdeckAPI.sources.get(results.models[0].id, {
      include: "config.auth"
    }) : results.models[0] : Promise.resolve(null));
    $[0] = HookdeckAPI.sources;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refreshInterval: _temp
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: source,
    mutate: mutateSources
  } = useSWR("guided_connection_create_source", t0, t1);
  let t2;
  if ($[3] !== HookdeckAPI.destinations) {
    t2 = () => HookdeckAPI.destinations.list({
      limit: 1
    }).then(results_0 => results_0.models.length > 0 ? HookdeckAPI.destinations.get(results_0.models[0].id, {
      include: "config.auth"
    }) : Promise.resolve(null));
    $[3] = HookdeckAPI.destinations;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      refreshInterval: _temp2
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: destination,
    mutate: mutateDestinations
  } = useSWR(source && "guided_connection_create_destination", t2, t3);
  let t4;
  let t5;
  if ($[6] !== destination || $[7] !== destination_type_committed || $[8] !== setDestinationTypeCommitted || $[9] !== setSelectedDestinationType) {
    t4 = () => {
      if (!destination || destination_type_committed) {
        return;
      }
      const destination_type = destination.type as DestinationType;
      setSelectedDestinationType(destination_type);
      setPreviewDestinationType(destination_type);
      setDestinationTypeCommitted(true);
    };
    t5 = [destination, destination_type_committed, setDestinationTypeCommitted, setSelectedDestinationType];
    $[6] = destination;
    $[7] = destination_type_committed;
    $[8] = setDestinationTypeCommitted;
    $[9] = setSelectedDestinationType;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== destination || $[13] !== source) {
    t6 = APIMethodKeys.webhooks.list(source && destination ? {
      limit: 1,
      source_id: source.id,
      destination_id: destination.id
    } : undefined);
    $[12] = destination;
    $[13] = source;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== HookdeckAPI.webhooks || $[16] !== destination?.id || $[17] !== source?.id) {
    t7 = () => HookdeckAPI.webhooks.list({
      limit: 1,
      source_id: source?.id,
      destination_id: destination?.id
    });
    $[15] = HookdeckAPI.webhooks;
    $[16] = destination?.id;
    $[17] = source?.id;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      refreshInterval: _temp3
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const {
    data: webhooks,
    mutate: mutateWebhooks
  } = useSWR(t6, t7, t8);
  const webhook = webhooks?.models[0];
  let t9;
  if ($[20] !== source) {
    t9 = source ? {
      source_id: [source.id]
    } : {};
    $[20] = source;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = source && !destination ? 1000 : 5000;
  const t11 = !!source;
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = {
      limit: 50,
      revalidation_interval: t10,
      include_data: true,
      fetch: t11
    };
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const requests_list = useRequestList(t9, t12);
  let t13;
  if ($[25] !== webhook) {
    t13 = webhook ? {
      webhook_id: [webhook.id],
      cli_id: {
        all: true
      }
    } : {};
    $[25] = webhook;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = !!webhook;
  let t15;
  if ($[27] !== t14) {
    t15 = {
      limit: 50,
      revalidation_interval: 2000,
      include_data: true,
      fetch: t14
    };
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const events_list = useEventList(t13, t15);
  const context = {
    source: source || null,
    destination: destination || null,
    selected_destination_type,
    preview_destination_type,
    destination_type_committed,
    webhook: webhook || null,
    requests_list: requests_list || null,
    events_list: events_list || null
  };
  const active_steps = steps.filter(step => !step.conditional || step.conditional(context) === true);
  let progressed_to_index = active_steps.findIndex(step_0 => !step_0.reveal ? false : step_0.reveal(context) === false);
  if (progressed_to_index === -1) {
    progressed_to_index = active_steps.length - 1;
  } else {
    progressed_to_index = progressed_to_index - 1;
  }
  useEffect(() => {
    const is_valid = active_steps.some(step_1 => step_1.key === completed_step);
    if (!is_valid) {
      const original_index = steps.findIndex(step_2 => step_2.key === completed_step);
      let fallback_key = active_steps[0].key;
      for (const active_step of active_steps) {
        const step_index = steps.findIndex(s => s.key === active_step.key);
        if (step_index < original_index) {
          fallback_key = active_step.key;
        }
      }
      setCompletedStep(fallback_key);
    }
  }, [active_steps, completed_step, setCompletedStep]);
  let t16;
  if ($[29] !== completed_step) {
    t16 = step_3 => step_3.key === completed_step;
    $[29] = completed_step;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const completed_to_index = active_steps.findIndex(t16);
  const progress = active_steps.reduce(_temp4, Math.min(progressed_to_index, completed_to_index + 1));
  let t17;
  if ($[31] !== progress) {
    t17 = (key, step_5, i_0) => {
      if (i_0 <= progress && step_5.nav_label) {
        return step_5.key;
      }
      return key;
    };
    $[31] = progress;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const current_nav_step_key = active_steps.reduce(t17, active_steps[0].key);
  let t18;
  if ($[33] !== active_steps || $[34] !== completed_step || $[35] !== progress) {
    t18 = {
      progress,
      steps: active_steps,
      completion_milestone: completed_step
    };
    $[33] = active_steps;
    $[34] = completed_step;
    $[35] = progress;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const {
    scroll_to_step_key
  } = useGuidedStepScroll(t18);
  const prev_tracked_completed_step_ref = useRef(null);
  const no_connection_retry_in_flight = useRef(false);
  let t19;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = new Set();
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const no_connection_auto_retried_request_ids = useRef(t19);
  let t20;
  if ($[38] !== active_steps || $[39] !== completed_step || $[40] !== progress) {
    t20 = [active_steps, completed_step, progress];
    $[38] = active_steps;
    $[39] = completed_step;
    $[40] = progress;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  useEffect(() => {
    if (prev_tracked_completed_step_ref.current === null) {
      prev_tracked_completed_step_ref.current = completed_step;
      return;
    }
    if (prev_tracked_completed_step_ref.current === completed_step) {
      return;
    }
    prev_tracked_completed_step_ref.current = completed_step;
    const step_key = active_steps[progress]?.key;
    if (step_key) {
      HookdeckAPI.track.event("Completed Guided Connection Step", {
        step: step_key
      });
    }
  }, t20);
  let t21;
  if ($[42] !== HookdeckAPI.requests || $[43] !== current_nav_step_key || $[44] !== events_list || $[45] !== requests_list || $[46] !== webhook) {
    t21 = () => {
      if (webhook && requests_list && requests_list.count > 0 && current_nav_step_key === "events") {
        const requests_to_retry = requests_list.requests.filter(request => request.rejection_cause === "NO_CONNECTION" && !no_connection_auto_retried_request_ids.current.has(request.id));
        if (requests_to_retry.length > 0 && !no_connection_retry_in_flight.current) {
          no_connection_retry_in_flight.current = true;
          for (const request_0 of requests_to_retry) {
            no_connection_auto_retried_request_ids.current.add(request_0.id);
          }
          const webhook_id = webhook.id;
          Promise.all(requests_to_retry.map(request_1 => HookdeckAPI.requests.retry(request_1.id, [webhook_id]))).then(() => {
            requests_list.refresh();
            events_list.refresh();
          }).finally(() => {
            no_connection_retry_in_flight.current = false;
          });
        }
      }
    };
    $[42] = HookdeckAPI.requests;
    $[43] = current_nav_step_key;
    $[44] = events_list;
    $[45] = requests_list;
    $[46] = webhook;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== HookdeckAPI || $[49] !== current_nav_step_key || $[50] !== requests_list || $[51] !== webhook) {
    t22 = [webhook, requests_list, current_nav_step_key, HookdeckAPI];
    $[48] = HookdeckAPI;
    $[49] = current_nav_step_key;
    $[50] = requests_list;
    $[51] = webhook;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  useEffect(t21, t22);
  let t23;
  if ($[53] !== active_steps) {
    t23 = active_steps.filter(_temp5);
    $[53] = active_steps;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  const t24 = t23 as {
    key: string;
    nav_label: string;
  }[];
  let t25;
  if ($[55] !== current_nav_step_key || $[56] !== t24) {
    t25 = <GuidedConnectionNav steps={t24} current_step={current_nav_step_key} />;
    $[55] = current_nav_step_key;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  const T0 = FullscreenLayout;
  const t26 = true;
  const t27 = true;
  let t28;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      t: 33
    };
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  const T1 = Container;
  const t29 = true;
  let t30;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      b: 14
    };
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Div m={t30}><Text size="2xl" bold={true} m={{
        b: 1
      }}>Create your first Hookdeck connection</Text><Text muted={true}>The guide below will help you get started with core aspects of the Hookdeck experience.</Text></Div>;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  const t32 = active_steps.map((step_7, index) => {
    if (progress + 1 < index) {
      return <span key={step_7.key} id={step_7.key} style={{
        position: "absolute",
        top: "-100px"
      }} />;
    }
    return <Div key={step_7.key} data-testid={`guided-step-${step_7.key}`} flex={true} style={progress < index ? {
      opacity: 0.2,
      pointerEvents: "none",
      position: "relative"
    } : {
      position: "relative"
    }}><span id={step_7.key} style={{
        position: "absolute",
        top: "-100px"
      }} /><StyledProgress required={!step_7.optional} is_last={active_steps.length - 1 === index} is_last_primary={true}><Icon p={0.5} icon={step_7.icon} /></StyledProgress><Div p={{
        l: 12,
        b: 20
      }} flex={{
        grow: true
      }} w={{
        px: 998
      }}><Div flex={{
          gap: 33
        }}><Div w={step_7.full_width ? 100 : {
            px: GUIDED_CONNECTION_WIDTHS.step_container
          }}>{!step_7.hide_header && <Div><Text bold={true} size="l" m={{
                b: 0
              }} flex={{
                align: "center"
              }}>{step_7.label_types || step_7.label}{step_7.optional && <Badge m={{
                  l: 3
                }} neutral={true}>Optional</Badge>}</Text><Text as="p" muted={true} m={{
                b: index === 0 ? 4 : 6
              }}>{step_7.description_types || step_7.description}</Text></Div>}{step_7.Component && <step_7.Component context={context as any} is_current_step={progress === index} nextStep={() => {
              let target_index = index;
              while (target_index + 1 < active_steps.length && (active_steps[target_index + 1].optional || active_steps[target_index + 1].skip_over)) {
                const next_scroll_target_index = target_index + 2;
                if (next_scroll_target_index < active_steps.length) {
                  const next_scroll_step = active_steps[next_scroll_target_index];
                  const is_visible = !next_scroll_step.reveal || next_scroll_step.reveal(context) === true;
                  if (!is_visible) {
                    break;
                  }
                }
                target_index++;
              }
              if (index > completed_to_index) {
                setCompletedStep(active_steps[target_index].key);
              }
              const scroll_target_index = Math.min(target_index + 1, active_steps.length - 1);
              const scroll_target_key = active_steps[scroll_target_index].key;
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  scroll_to_step_key(scroll_target_key);
                });
              });
            }} mutateContext={input => {
              if (input.source) {
                mutateSources(input.source);
              }
              if (input.destination) {
                mutateDestinations(input.destination);
              }
              if (input.preview_destination_type) {
                setPreviewDestinationType(input.preview_destination_type);
              }
              if (input.selected_destination_type) {
                setSelectedDestinationType(input.selected_destination_type);
              }
              if (typeof input.destination_type_committed === "boolean") {
                setDestinationTypeCommitted(input.destination_type_committed);
              }
              if (input.webhook) {
                mutateHasConnection();
                mutateWebhooks({
                  ...webhooks,
                  models: [input.webhook]
                });
              }
              if (input.request) {
                requests_list.refresh(input.request);
              }
              if (input.event) {
                events_list.refresh(input.event);
              }
            }} />}</Div>{step_7.reference && <Div w={{
            px: GUIDED_CONNECTION_WIDTHS.reference_container
          }}><Div flex={{
              justify: "flex-end"
            }}><StyledCard overflow_hidden={true}>{step_7.reference.image && <StyledCardSection style={{
                  lineHeight: 0
                }}><img src={step_7.reference.image?.(theme.mode) || ""} alt={step_7.reference.title} width={GUIDED_CONNECTION_WIDTHS.reference_container} height={160} /></StyledCardSection>}<StyledCardSection p={4}><Text as="p" muted={true} size="s" m={{
                    b: 4
                  }}>{step_7.reference.text}</Text><Link small={true} neutral={true} href={step_7.reference.link} icon="link" target="_blank">{`${step_7.reference.title} docs` || "Docs"}</Link></StyledCardSection></StyledCard></Div></Div>}</Div></Div></Div>;
  });
  let t33;
  if ($[61] !== T1 || $[62] !== t31 || $[63] !== t32) {
    t33 = <T1 xxlarge={t29}>{t31}{t32}</T1>;
    $[61] = T1;
    $[62] = t31;
    $[63] = t32;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] !== T0 || $[66] !== t28 || $[67] !== t33) {
    t34 = <T0 light={t26} no_decorations={t27} p={t28}>{t33}</T0>;
    $[65] = T0;
    $[66] = t28;
    $[67] = t33;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  let t35;
  if ($[69] !== t25 || $[70] !== t34) {
    t35 = <>{t25}{t34}</>;
    $[69] = t25;
    $[70] = t34;
    $[71] = t35;
  } else {
    t35 = $[71];
  }
  return t35;
};
export default GuidedCreateConnection;
function _temp(result) {
  return !result ? 5000 : 0;
}
function _temp2(result_0) {
  return !result_0 ? 5000 : 0;
}
function _temp3(result_1) {
  return !result_1 ? 5000 : 0;
}
function _temp4(p, step_4, i) {
  return i === p + 1 && step_4.skip_over ? i : p;
}
function _temp5(step_6) {
  return !!step_6.nav_label;
}